// Vue core libraries - Pinia & Vue
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { DateTime } from "luxon";

// TMI - content type and query definitions
import { useApplicationStore } from "@/stores/Application";
import { usePermissionsStore } from "@/stores/Permissions";
import { composeBonusReport } from "@/services/data/sync/bonus-reports-sync-coordinator";
import { isFreelyAvailableContent } from "@/utils/access-utils";

import { goFetch } from "@/utils/network-utils";
import { clientStateManager } from "@/services/error-services";

import type { PersistedQueryPayload } from "@/types/api/fetch";
import type { BonusReports } from "@/types/api/content-models";
import LocalForagePlugin, { bonusReports } from "@/plugins/LocalForagePlugin";
import {
    BonusReport,
    Category,
    ContentfulBonusReport,
    ContentfulItem,
    Countable,
    Product
} from "@/types/entities";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
import { formatDateAsISO8601String } from "@/helpers/dateTimeUtilities";
import { useAuthStore } from "@/stores/Auth";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

// This is implemented as a computed property to avoid unnecessarily calculating it multiple times
const bonusReportCategories = computed< Category[] >( () => {
    // Contains the full catalog of all articles the current user has access to with all dependent objects associated
    const { allBonusReports } = useBonusReportsStore();

    const categoryCountMap: Map< string, Countable< Category > > = new Map< string, Countable< Category > >();
    allBonusReports.forEach( ( item: ContentfulItem ): void => {
        item.categories.forEach( ( category: Category ): void => {
            if ( !categoryCountMap.has( category.slug ) ) {
                categoryCountMap.set( category.slug, {
                    item: category,
                    count: 0
                } );
            }

            categoryCountMap.get( category.slug )!.count++;
        } );
    } );

    const categoriesByObservationCount: Countable< Category >[] = [ ...categoryCountMap.values() ];
    const categoriesByAscendingObservationCount: Countable< Category >[] =
        categoriesByObservationCount.sort( ( container1: Countable< Category >, container2: Countable< Category > ) => container1.count - container2.count );

    return categoriesByAscendingObservationCount.map( ( container: Countable< Category > ) => container.item );
} );

// State values
const isLoading = computed< boolean >( () => loading.value );

const isInitialized = computed< boolean >( () => allBonusReports.value.length > 0 );
const loading = ref< boolean >( false );

const allBonusReportsBySlugMap = new Map< string, BonusReport >();
const allBonusReportsByIdMap = new Map< string, BonusReport >();
const allBonusReports = ref< BonusReport[] >( [] );

export const useBonusReportsStore = defineStore( 'bonusReports', () => {
    // Depends on appStore for authentication state and to signal that data is being loaded
    const appStore = useApplicationStore();
    const { authToken, isAuthenticated } = storeToRefs( useAuthStore() );
    const permissionsStore = usePermissionsStore();

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        // Reset the tracking for unique subscription articles
        loading.value = false;

        // Reset the aggregate collection of all articles
        allBonusReportsBySlugMap.clear();
        allBonusReportsByIdMap.clear();
        allBonusReports.value = [];
    }

    function bonusReportBySlug( slug: string ): BonusReport | undefined {
        return allBonusReportsBySlugMap.get( slug );
    }

    function bonusReportById( id: string ): BonusReport | undefined {
        return allBonusReportsByIdMap.get( id );
    }

    /**
     * Load all data that is stored within the local IndexedDB or request data from API if not present
     */
    function initialize(): void {
        if ( !isAuthenticated.value || isInitialized.value || loading.value ) {
            return;
        }

        loading.value = true;
        const t0: number = performance.now();

        if ( typeof bonusReports === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        bonusReports.ready()
            .then( () => {
                bonusReports.length()
                    .then( ( val: number ) => {
                        if ( val > 0 ) {
                            const loadedReports: BonusReport[] = [];
                            // Iterate over all elements within IDB
                            bonusReports.iterate( ( br: BonusReport ) => {
                                loadedReports.push( br );
                                return;
                            } ).then( () => {
                                mergeAndSortBonusReportsCatalog( loadedReports );

                                const t1: number = performance.now();
                                logger.info( `(local) Loaded ${ loadedReports.length } bonus reports in ${ t1 - t0 } ms` );
                            } ).catch( ( err: any ) => {
                                logger.error( JSON.stringify( err.message ) );
                            } ).finally( () => {
                                // Clear the "private loading" flag
                                loading.value = false;
                            } );
                        } else {
                            const startingDate = formatDateAsISO8601String( DateTime.fromFormat( '2020-01-01', 'yyyy-MM-dd' ).toUTC() );
                            refreshBonusReportsFromServer( startingDate )
                                .then( ( reports: BonusReport[] ) => {
                                    mergeAndSortBonusReportsCatalog( reports );

                                    const t1: number = performance.now();
                                    logger.info( `Fetching & mapping ${ reports.length } bonus reports took ${ t1 - t0 } ms` );
                                } )
                                .catch( () => {
                                    // If anything went wrong while attempting to initialize the store, reset it and then handle the specific error
                                    reset();
                                } )
                                .finally( () => {
                                    // Clear the "private loading" flag
                                    loading.value = false;
                                } );
                        }
                    } )
            } );
    }

    async function refreshBonusReportsFromServer( latestUtcDate: string ): Promise< BonusReport[] > {
        let retrievedReports: BonusReport[] = [];
        try {
            const response =
                await goFetch< BonusReports, PersistedQueryPayload >( {
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: 'dcb63ca81a12007ee8b590e75cd82457',
                        variables: {
                            skip: 0,
                            limit: 250,
                            contentFilter: {
                                fromPublishedDateTimeUTC: latestUtcDate
                            }
                        }
                    },
                    authToken: authToken.value
                } );

            if ( null != response?.data?.bonusReportCatalog?.bonus_reports ) {
                response.data.bonusReportCatalog.bonus_reports
                    .map( ( r: ContentfulBonusReport ) => {
                        const report: BonusReport | undefined = composeBonusReport( r, appStore.compositionCtx );
                        if ( report ) {
                            retrievedReports.push( report );
                        }
                    } );
            }
        } catch ( err: any ) {
            // If anything went wrong while attempting to initialize the store, reset it and then handle the specific error
            reset();
            clientStateManager.queueClientError( err, {
                startingDate: latestUtcDate
            } );
        } finally {
            loading.value = false;
        }

        return retrievedReports;
    }

    function mergeAndSortBonusReportsCatalog( newBonusReports: BonusReport[] ): void {
        newBonusReports.forEach( ( newReport: BonusReport ): void => {
            if ( !allBonusReportsByIdMap.has( newReport.contentfulId ) ) {
                allBonusReportsByIdMap.set( newReport.contentfulId, newReport );
            }

            if ( !allBonusReportsBySlugMap.has( newReport.slug ) ) {
                allBonusReportsBySlugMap.set( newReport.slug, newReport );

                // Filter out any content to which the current user has not been granted access
                const hasAccess = newReport.products.some( ( p: Product ) =>
                    !!( p.codes ) && ( permissionsStore.hasAccessBasedOnShortCode( p.codes ) || isFreelyAvailableContent( p.slug ) )
                );

                if ( hasAccess ) {
                    allBonusReports.value.push( newReport );
                }
            }
        } );

        allBonusReports.value.sort( ( r1: BonusReport, r2: BonusReport ) => r1.publishedDate < r2.publishedDate ? 1:-1 );
    }

    return {
        // ACTIONS
        bonusReportById,
        bonusReportBySlug,
        bonusReportCategories,
        initialize,
        reset,

        // GETTERS
        allBonusReports,
        isLoading
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useBonusReportsStore, import.meta.hot ) );
}
